import {showNotification} from 'platform/components';

import {getApiErrorMessages} from '@dms/api/utils';

import {ApiError} from 'shared/web';

import {getApiValidationErrors} from './getApiValidationErrors';

type ValidationErrors = ReturnType<typeof getApiValidationErrors>;

type OptionsCallback = {
  error: ApiError;
  validationErrors: ValidationErrors;
};

export type ApiErrorOptions = {
  silent?: boolean;
  silentOnValidationError?: boolean;
  callback?: (args: OptionsCallback) => void;
  setErrors?: (errors: ValidationErrors) => void;
  /**
   * Called after the error is handled to signal that an API error occurred.
   * Pass `blockerContext?.reportApiFailure` from `FormSubmitHandler` to prevent
   * the UnsavedChangesBlocker from navigating away when a submit fails.
   *
   * @example
   * const handleSubmit: FormSubmitHandler<MyForm> = async (values, setErrors, reset, blockerContext) => {
   *   await mutation().unwrap()
   *     .then(() => showNotification.success())
   *     .catch((e: ApiError) => handleApiError(e, {signalApiError: blockerContext?.reportApiFailure}));
   * };
   */
  signalApiError?: () => void;
};

/**
 * @about Handle API errors by showing toast notifications with error messages.
 * Note: Sentry error reporting is handled automatically by the base query layer for server errors.
 */
export function handleApiError(error: ApiError, options?: ApiErrorOptions) {
  const validationErrors = getApiValidationErrors(error);

  options?.callback?.({error, validationErrors});

  options?.setErrors?.(validationErrors);

  if (options?.silent || (options?.silentOnValidationError && !!validationErrors)) {
    options?.signalApiError?.();
    return;
  }

  showNotification.error(getApiErrorMessages(error));

  options?.signalApiError?.();
}
